import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { getRemotedbUrl, i18nIntl, isNotEmpty } from '../../../lib/ep.js';

import ActionButton, { MainButton, SecondaryButton, SquaredButton } from '../../builder/ActionButton';
import ActionLink from '../../builder/ActionLink';
import ButtonCollection from '../../builder/ButtonCollection.js';
import RadioGroup from '../../builder/RadioGroup';
import FloatingLayer from '../../builder/FloatingLayer';
import Message, { AlertMessage } from '../../builder/Message';
import Model from '../../builder/Model';
import SaveButton from '../../builder/SaveButton';
import SectionSubtitle from '../../builder/SectionSubtitle';
import SectionTitle from '../../builder/SectionTitle';

const DisableTestModeLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const has_media = props?.has_media ?? false;
	const is_pick_your_favorite = props?.is_pick_your_favorite ?? false;
	const media_data = 'keep';
	const type_action_media_data = [{
		value:'keep',
		label:"Keep it, if it's definitive content that you want your participants to see or vote for. We do reset the vote counters.",
	}, {
		value:'remove',
		label:"Remove it, if they were only tests with test data.",
	}];

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			enable_close={false}
		>
			<div>
				<SectionTitle>Do you want to disable test mode?</SectionTitle>

				{(!has_media) ? (
					<SectionSubtitle>When you disable test mode, all the test data is deleted and the stats, users and prizes will be reset.</SectionSubtitle>
				) : (
					<AlertMessage
						title="Attention!"
						_class={{ other:'in_top tip_message' }}
					>
						<p>{i18n("We have detected that in the test mode you have uploaded multimedia content. Do you want to keep it or remove it by disabling the test?")}</p>

						<Model model={props.media_model} change={props.changeMediaModel}>
							<RadioGroup
								change={(k, v) => change(k, v)}
								elements={type_action_media_data}
								default={media_data}
								name="media_data"
								_class={{ first:true }}
							/>
						</Model>
					</AlertMessage>
				)}

				{(is_pick_your_favorite) && (
					<Message
						_class={{ other:'in_top' }}
					>
						<p>{i18n("The participants will be maintained and the vote count will be set to 0.")}</p>
					</Message>
				)}

				<Message
					_class={{ other:'in_top in_bottom' }}
					title="You can enable test mode again as long as the promotion is still in draft."
				/>

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={(e, cb) => props.toggleTest(false, cb)}
					>Disable</SaveButton>

					<SecondaryButton
						onClick={props.closeLayer}
					>Cancel</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const EnableTestModeLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const current_active = props?.current_active ?? 0;
	const has_media = props?.has_media ?? false;
	const is_pick_your_favorite = props?.is_pick_your_favorite ?? false;

	if(current_active > 0 && !has_media) {
		return (
			<FloatingLayer
				_class={{ size:'small' }}
				backButton={props.closeLayer}
				enable_close={false}
			>
				<div>
					<SectionTitle
						_class={{
							icon:'knk-alert',
							other:'redColor',
						}}
					>Warning!</SectionTitle>

					<FormattedMessage id="You must delete the current {delete} in the promotion to enable the test mode." values={{delete:is_pick_your_favorite ? i18n("votes") : i18n("users")}} />

					<ButtonCollection in_floating={true}>
						<MainButton
							_href={getRemotedbUrl(`/users/registered/${props.promotion_id}`)}
						>Go to the list of Participants</MainButton>

						<SecondaryButton
							onClick={props.closeLayer}
						>Cancel</SecondaryButton>
					</ButtonCollection>
				</div>
			</FloatingLayer>
		)
	}

	const agent = window.owner_main_vars?.creator?.Agent;

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			backButton={props.closeLayer}
			enable_close={false}
		>
			<div>
				<SectionTitle>Test mode</SectionTitle>

				<SectionSubtitle>You can participate as a real user and test all features. When you finish testing, you can delete all users and participations, reset stats, and release any prizes won during the test.</SectionSubtitle>

				<div class="gui_grid">
					<div>
						<p class="gui_underlined_text">
							<strong>{`${i18n("Security note")}:`}</strong>
						</p>

						<p>{`${i18n("Some options are limited due to security reasons until the promotion is activated")}.`}</p>

						<ol class="gui_not_in_top_tiny gui_not_in_bottom_small" style={{ display:'grid', gap:'0.2em' }}>
							<li>{i18n("Sharing is disabled.")}</li>
							
							<li
								dangerouslySetInnerHTML={{
									__html:props.intl.formatMessage({
										id:"All emails will be sent to the email address{email}."
									}, {
										email:`: <span class="blueColor">${agent?.email ?? "Unknown"}</span>`,
									})
								}}
							/>
						</ol>

						<p>
							<ActionLink
								_href={`${window.zendesk_base_url}360043826352/#10`}
								_target='_blank'
							>More information</ActionLink>
						</p>
					</div>
				</div>

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={(e, cb) => props.toggleTest(true, cb)}
					>Enable</SaveButton>

					<SecondaryButton
						onClick={props.closeLayer}
					>Cancel</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const AttentionLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			enable_close={false}
		>
			<div>
				<SectionTitle
					_class={{ icon:'knk-alert', other:'redColor' }}
				>Warning!</SectionTitle>

				<SectionSubtitle>Your promotion is in test mode. You must disable the test mode to continue with the activation of the promotion.</SectionSubtitle>

				<SectionSubtitle>When you disable test mode, all the test data is deleted and the stats, users and prizes will be reset.</SectionSubtitle>

				{(is_pick_your_favorite) && (
					<Message
						_class={{ other:'in_top' }}
					>
						<p>{i18n("The participants will be maintained and the vote count will be set to 0.")}</p>
					</Message>
				)}

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={(e, cb) => props.toggleTest(false, cb)}
					>Disable test mode</SaveButton>

					<SecondaryButton
						onClick={props.closeLayer}
					>Cancel</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const Wrapper = (props) => {
	const is_test = props?.is_test ?? false;

	const classes = ['in_top'];
	if(isNotEmpty(props, 'extra_classes')) classes.push(props.extra_classes);

	return (
		<div class={classes.join(" ")}>
			{props.children}

			{(props?.display_toggle_layer ?? false) && (
				<>
					{(is_test) && (
						<DisableTestModeLayer
							{...props.default_layer_props}
							closeLayer={props.closeToggleLayer}
						/>
					)}

					{(!is_test) && (
						<EnableTestModeLayer
							{...props.default_layer_props}
							closeLayer={props.closeToggleLayer}
						/>
					)}
				</>
			)}

			{(props?.display_attention_layer ?? false) && (
				<AttentionLayer
					{...props.default_layer_props}
					closeLayer={props.closeAttentionLayer}
				/>
			)}
		</div>
	)
};

const TestEnableButtonBuilderHeader = (props) => {
	if(props?.is_mobile_version ?? false) {
		return (
			<SquaredButton
				color="variable"
				icon='knk-developers'
				_class={{ other:'blackIcon whiteBg' }}
				onClick={() => props.toggleLayer(true)}
				aria-label="Enable test mode"
			/>
		)
	}
	let Component = SecondaryButton;
	if(props.action_button)Component = ActionButton;
	return (
		<Component
			icon={!props.action_button ? 'knk-slide-off' : null}
			onClick={() => props.toggleLayer(true)}
		>Enable test mode</Component>
	)
};

const GoToTestURLButtonBuilderHeader = ({ url, ...props }) => {
	if(props?.is_mobile_version ?? false) {
		return (
			<SquaredButton
				color="variable"
				icon='knk-developers'
				_class={{ other:'blackIcon whiteBg' }}
				_href={url}
				_target="_blank"
				aria-label="Go to test mode"
			/>
		)
	}

	return (
		<SecondaryButton
			icon='knk-developers'
			_href={url}
			_target="_blank"
		>Go to test mode</SecondaryButton>
	)
};

const TestPromoButton = (props) => {
	const promotion = props?.promotion ?? false;
	const has_media = props?.has_media ?? false;
	const is_test = promotion?.test ?? false;
	const is_pick_your_favorite = (promotion.promotion_type_id == 13) ? true : false;
	const is_builder_header = props?.is_builder ?? false;
	const is_mobile_version = props?.is_mobile_version ?? false;

	const [ display_toggle_layer, setDisplayToggleLayer ] = useState(false);
	const [ display_attention_layer, setDisplayAttentionLayer ] = useState(false);
	
	const [ media_model, setMediaModel ] = useState({ media_data:'keep' });

	if(promotion === false) return null;
	if(promotion.promo_type < 1) return null; //NO basic test

	const changeMediaModel = (k, v) => {
		setMediaModel({ ...media_model, [k]:v });
	};

	const toggleTest = (enable, cb) => {
		$.post(getRemotedbUrl(`/promotions/check_field_promotion/${promotion.id}`), {
			value:'test'
		}, (res) => {
			if(enable != res.value) {
				//If I want to enable and is not enabled
				const send_data = {};
				if(has_media) send_data.media_data = media_model.media_data;

				$.post(getRemotedbUrl(`/promotions/turn_promotion_test/${promotion.id}`), send_data,
				(r) => {
					if(r.status) {
						promotion.test = (promotion.test != undefined) ? !promotion.test : true;
						if(props.onChangeTest != undefined) props.onChangeTest(promotion.test);
					}

					if(cb != undefined) cb();
					setDisplayToggleLayer(false);
				}, 'JSON');
			} else {
				if(props.onChangeTest != undefined) props.onChangeTest(enable);
				if(cb != undefined) cb();
				setDisplayToggleLayer(false);
			}
		}, 'JSON');
	};

	const default_wrapper_props = {
		is_test,
		default_layer_props:{
			is_pick_your_favorite,
			has_media,
			promotion_id:promotion.id,
			toggleTest,
			current_active:props.current_active,
			media_model,
			changeMediaModel,
			intl:props.intl,
		},
		closeToggleLayer:() => setDisplayToggleLayer(false),
		closeAttentionLayer:() => setDisplayAttentionLayer(false),
		display_toggle_layer,
		display_attention_layer,
	};

	if(is_builder_header) {
		return (
			<Wrapper {...default_wrapper_props}>
				<div class="gui_flexbox gui_flex_row gui_flex_align_center">
					{(is_test) && (
						<GoToTestURLButtonBuilderHeader
							url={getRemotedbUrl(`${window.owner_url}p/${promotion.id}?utm_source=test`, null, false)}
							is_mobile_version={is_mobile_version}
						/>
					)}

					{(!is_test) && (
						<TestEnableButtonBuilderHeader
							action_button={props?.action_button ?? false}
							is_mobile_version={is_mobile_version}
							toggleLayer={setDisplayToggleLayer}
						/>
					)}
				</div>
			</Wrapper>
		)
	}

	if(is_test) {
		return (
			<Wrapper extra_classes='gui_not_in_bottom_tiny' {...default_wrapper_props}>
				<MainButton
					icon='knk-slide-on'
					onClick={() => setDisplayToggleLayer(true)}
				>Disable test mode</MainButton>
			</Wrapper>
		)
	}
	
	return (
		<Wrapper {...default_wrapper_props}>
			<div class="gui_module_with_header gui_postit gui_is_padded_low gui_not_in_top_tiny">
				<p>
					<FormattedMessage
						id="Do you want to test the promotion before activating it? {enable_test_mode} and make sure everything works properly."
						values={{
							enable_test_mode:<ActionLink
								onClick={() => setDisplayToggleLayer(true)}
								_class={{ other:'gui_force_bold' }}
							>Enable test mode</ActionLink>,
						}}
					/>
				</p>
			</div>
		</Wrapper>
	)
};

export default injectIntl(TestPromoButton);